if (!customElements.get("m-countdown-timer")) {
  class MCountdownTimer extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      const mode = this.dataset.countdownMode || "fixed";
      if (mode === "rolling_24") {
        this.startRollingCountdown();
        return;
      }

      this.startFixedCountdown();
    }

    startFixedCountdown() {
      const time = this.dataset.time;
      const endTime = this.parseCountdownTime(time);
      if (Number.isFinite(endTime)) {
        const handleComplete = () => {
          this.dataset.countdownEnded = "true";
          window.requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent("countdown:ended", { bubbles: true }));
          });
        };

        if (endTime <= Date.now()) {
          handleComplete();
          return;
        }

        new CountdownTimer(this, Date.now(), endTime, {
          callback: handleComplete,
        });

        this.classList.remove("hidden");
        this.classList.remove("m:hidden");
      }
    }

    startRollingCountdown() {
      if (this.countdownTimer?.timer) {
        clearInterval(this.countdownTimer.timer);
      }
      window.clearTimeout(this.rollingTimeout);

      const now = Date.now();
      const endTime = this.getNextDailyEndTime(now);
      if (!Number.isFinite(endTime)) return;

      this.dataset.countdownEnded = "false";
      this.countdownTimer = new CountdownTimer(this, now, endTime, {
        callback: () => {
          this.startRollingCountdown();
        },
      });
      this.rollingTimeout = window.setTimeout(() => {
        this.startRollingCountdown();
      }, Math.max(endTime - now, 0));

      this.classList.remove("hidden");
      this.classList.remove("m:hidden");
    }

    parseCountdownTime(value) {
      const match = (value || "").match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})([+-])(\d{2}):(\d{2})$/);
      if (!match) return Date.parse((value || "").split(" ").join("T"));

      const offsetSign = match[7] === "+" ? 1 : -1;
      const offsetMinutes = offsetSign * (Number(match[8]) * 60 + Number(match[9]));
      const utcTime = Date.UTC(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6])
      );

      return utcTime - offsetMinutes * 60 * 1000;
    }

    getNextDailyEndTime(now) {
      const resetTime = this.parseResetTime(this.dataset.countdownResetTime || this.dataset.time || "00:00");
      if (!resetTime) return NaN;

      if (this.dataset.countdownBrowserTimezone === "true") {
        const localNow = new Date(now);
        let nextEndTime = new Date(
          localNow.getFullYear(),
          localNow.getMonth(),
          localNow.getDate(),
          resetTime.hour,
          resetTime.minute,
          resetTime.second
        ).getTime();

        if (nextEndTime <= now) {
          nextEndTime += 24 * 60 * 60 * 1000;
        }

        return nextEndTime;
      }

      const offsetMinutes = this.parseTimezoneOffset(this.dataset.countdownTimezone || "+00:00");
      if (!Number.isFinite(offsetMinutes)) return NaN;

      const offsetMs = offsetMinutes * 60 * 1000;
      const zonedNow = new Date(now + offsetMs);
      let nextEndTime = Date.UTC(
        zonedNow.getUTCFullYear(),
        zonedNow.getUTCMonth(),
        zonedNow.getUTCDate(),
        resetTime.hour,
        resetTime.minute,
        resetTime.second
      ) - offsetMs;

      if (nextEndTime <= now) {
        nextEndTime += 24 * 60 * 60 * 1000;
      }

      return nextEndTime;
    }

    parseResetTime(value) {
      const match = (value || "").trim().match(/^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm)?$/i);
      if (!match) return null;

      let hour = Number(match[1]);
      const minute = Number(match[2] || 0);
      const second = Number(match[3] || 0);
      const meridiem = match[4] ? match[4].toLowerCase() : "";

      if (meridiem === "pm" && hour < 12) hour += 12;
      if (meridiem === "am" && hour === 12) hour = 0;
      if (hour > 23 || minute > 59 || second > 59) return null;

      return { hour, minute, second };
    }

    parseTimezoneOffset(value) {
      const match = (value || "").trim().match(/^([+-])(\d{2}):?(\d{2})$/);
      if (!match) return NaN;

      const sign = match[1] === "+" ? 1 : -1;
      return sign * (Number(match[2]) * 60 + Number(match[3]));
    }
  }

  customElements.define("m-countdown-timer", MCountdownTimer);
}
